import axios from "axios";
import React, { createContext, useContext, useEffect, useState } from "react";
import companyStore from "../Content/CompanyStore";
import config from "../../service/config";
import { jwtDecode } from "jwt-decode";
import { useLocation, useNavigate } from "react-router-dom";
import { CognitoIdentityProviderClient, InitiateAuthCommand } from "@aws-sdk/client-cognito-identity-provider";

const AllDivisionContext = createContext([]);

export const ApiProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize state with localStorage values
  const [eSignCoordinates, setESignCoordinates] = useState(() => {
    const savedData = localStorage.getItem("signCoordinates");
    return savedData ? JSON.parse(savedData) : [];
  });
  const [role, setRole] = useState([]);
  const [clientID, setClientID] = useState("");
  const [userName, setUserName] = useState("");
  const [loader, setLoader] = useState(false);
  const [authTokens, setAuthTokens] = useState(() => {
    const savedTokens = localStorage.getItem("authTokens");
    return savedTokens ? JSON.parse(savedTokens) : { idToken: "", refreshToken: "" };
  });
  const [authFromUrl, setAuthFromUrl] = useState("");
  const [userData, setUserData] = useState([]);
  const [refreshData, setRefreshData] = useState(false);

  const isTokenValid = (token) => {
    try {
      const decoded = jwtDecode(token);
      return decoded.exp * 1000 > Date.now();
    } catch (e) {
      console.error("Invalid token:", e.message);
      return false;
    }
  };

  const getNewTokens = async () => {
    console.log("Refreshing tokens...");
    try {
      const client = new CognitoIdentityProviderClient({ region: "ap-south-1" });
      const params = {
        ClientId: config.cognito_client_id,
        AuthFlow: "REFRESH_TOKEN_AUTH",
        AuthParameters: {
          REFRESH_TOKEN: authTokens.refreshToken,
        },
      };
      const command = new InitiateAuthCommand(params);
      const refreshResponse = await client.send(command);

      if (refreshResponse.AuthenticationResult) {
        const newTokens = {
          refreshToken: authTokens.refreshToken,
          idToken: refreshResponse.AuthenticationResult.IdToken,
          accessToken: refreshResponse.AuthenticationResult.AccessToken,
        };
        localStorage.setItem("authTokens", JSON.stringify(newTokens));
        setAuthTokens(newTokens);
        return newTokens;
      } else {
        console.log("Unable to refresh tokens.");
        navigate("/");
      }
    } catch (error) {
      console.error("Error refreshing tokens:", error);
      navigate("/");
    }
  };

  // Fetch URL params on mount or location change
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const idToken = queryParams.get("idToken");
    if (idToken) {
      setAuthFromUrl(idToken);
      console.log("Access Token from URL:", idToken);
    } else {
      console.log("Access Token not found in URL");
    }
  }, [location.search]);

  // Sync eSignCoordinates with localStorage
  useEffect(() => {
    if (eSignCoordinates) {
      localStorage.setItem("signCoordinates", JSON.stringify(eSignCoordinates));
    }
  }, [eSignCoordinates]);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("authTokens"));
    if (!stored?.idToken && !location.pathname.includes("editor-contract")) {
      navigate("/");
    }
  }, []);

  // Fetch user data
  const getAllUsersData = async () => {
    setLoader(true);

    const MAX_RETRIES = 3;

    const delay = (ms) => new Promise((res) => setTimeout(res, ms));

    const attemptRequest = async (retryCount = 0) => {
      try {
        let currentTokens = JSON.parse(localStorage.getItem("authTokens"));

        // Refresh token if invalid
        if (!currentTokens?.idToken || !isTokenValid(currentTokens.idToken)) {
          currentTokens = await getNewTokens();
        }

        const response = await axios.get(`${config.node_api_endpoint}/v1/user/getAllUserData`, {
          headers: {
            aws_cognito_id_token: currentTokens.idToken,
            "Content-Type": "application/json",
          },
        });

        // SUCCESS
        const users = response.data.userData;
        setUserData(users);

        const loggedUser = users?.find((u) => u.email === companyStore.AdminEmail && u.status === "active");

        if (!loggedUser) {
          localStorage.clear();
          navigate("/");
          return;
        }

        const client = loggedUser.auth[0]?.clientId;
        if (client) setClientID(client);

        setRole(loggedUser.auth[0].role);
      } catch (error) {
        console.error(`Attempt ${retryCount + 1} failed →`, error);

        // FORCE LOGOUT ON 401/403
        if (error.response?.status === 401 || error.response?.status === 403) {
          console.error("Unauthorized — logging out");
          localStorage.clear();
          navigate("/");
          return;
        }

        // RETRY CONDITIONS
        const shouldRetry = retryCount < MAX_RETRIES && (!error.response || error.response.status >= 500); // network error OR server error

        if (shouldRetry) {
          const backoff = 200 * Math.pow(2, retryCount); // 200ms, 400ms, 800ms
          console.log(`Retrying in ${backoff}ms...`);
          await delay(backoff);
          return attemptRequest(retryCount + 1);
        }

        // FINAL FAILURE → Logout
        console.error("Max retries reached. Redirecting to login.");
        localStorage.clear();
        navigate("/");
      }
    };

    try {
      await attemptRequest(0);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (!location.pathname.includes("editor-contract")) {
      getAllUsersData();
    }
  }, [refreshData,authTokens]);

  return (
    <AllDivisionContext.Provider
      value={{
        userData,
        eSignCoordinates,
        setESignCoordinates,
        role,
        userName,
        setRole,
        setUserName,
        loader,
        setLoader,
        authTokens,
        setAuthTokens,
        authFromUrl,
        setUserData,
        setRefreshData,
        refreshData,
        clientID,
        isTokenValid,
        getNewTokens,
      }}
    >
      {children}
    </AllDivisionContext.Provider>
  );
};

export const useAllProduct = () => {
  const context = useContext(AllDivisionContext);
  if (!context) {
    throw new Error("useAllProduct must be used within an ApiProvider");
  }
  return context;
};

export default AllDivisionContext;
